import React, { useMemo, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { renderToStaticMarkup } from "react-dom/server";
import {
  MapPin, RefreshCw, Maximize2, ArrowLeft, ChevronRight,
} from "lucide-react";
import { PageShell, PageHeader } from "@/components/PageHeader";
import { RoleGate } from "@/components/RoleGate";
import { StatusPill } from "@/components/Chips";
import { GoogleMap } from "@/lib/maps/primitives";
import { useFleetLocations } from "@/lib/fleetLocations";

/**
 * FleetMapWorkspace — Fleet Map UX Hardening (Wave scope).
 *
 * Full-page large Google Map plus a synchronized "Current Asset
 * Locations" list. Both surfaces consume the SAME `useFleetLocations`
 * shared store so there is exactly one telemetry fetch cycle across
 * Dashboard preview + this workspace (and any future consumer).
 *
 * Behaviors preserved from the brief:
 *   - Initial load: fit to all valid markers
 *   - Filter change: refit visible markers
 *   - User pan/zoom: RESPECTED — refreshes update marker positions in
 *     place without resetting viewport. Fit Fleet re-fits explicitly.
 *   - Marker click → row select + InfoWindow
 *   - Row click (with coords) → focus map + open InfoWindow
 *   - Row click (no coords) → row highlights only; map does NOT move
 *   - Manual Refresh — spam-guarded (15s cooldown after success)
 *   - Failure: keep last valid rows + banner + Retry
 *
 * NOT this wave: Geocoding, Roads, Weather, WebSockets, admin
 * refresh-interval setting.
 */

const READ_ROLES = ["super_admin", "admin", "leasing_sales", "ops_dispatch", "accounting", "maintenance"];

// Filter → predicate. Uses ONLY operational_status which is already
// on the asset row — no new business math.
const FILTER_PREDICATES = {
  all:       () => true,
  available: (r) => r.operational_status === "available",
  touring:   (r) => r.operational_status === "touring",
  service:   (r) => ["maintenance", "shop", "in_service", "out_of_service"].includes(r.operational_status),
};

const FILTER_LABELS = [
  ["all",       "All"],
  ["available", "Available"],
  ["touring",   "Touring"],
  ["service",   "Service or Maintenance"],
];

function fmtTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString(undefined, {
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return "—";
  }
}

function relativeAgeLabel(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const mins = Math.max(0, Math.floor((Date.now() - then) / 60000));
  if (mins < 1) return "just now";
  if (mins === 1) return "1 min ago";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  return hrs === 1 ? "1 hr ago" : `${hrs} hrs ago`;
}

function buildInfoHtml(row) {
  const info = renderToStaticMarkup(
    <div style={{ fontSize: 12, lineHeight: 1.45, minWidth: 200 }}>
      <div style={{ fontWeight: 600 }}>{row.coach_label || row.fleet_number}</div>
      <div style={{ color: "#6B747C" }}>{row.location_label || "Location unavailable"}</div>
      {typeof row.speed_mph === "number" && (
        <div style={{ color: "#6B747C", marginTop: 2 }}>
          {row.speed_mph} mph · {row.ignition_on ? "ignition on" : "parked"}
        </div>
      )}
      {row.current_driver?.display_name && (
        <div style={{ color: "#4A5157", marginTop: 4 }}>
          Driver: {row.current_driver.display_name}
        </div>
      )}
      {row.last_updated_at && (
        <div style={{ color: "#6B747C", marginTop: 4, fontSize: 11 }}>
          Motive · {relativeAgeLabel(row.last_updated_at)}
        </div>
      )}
    </div>
  );
  return `<div>${info}</div>`;
}

const FreshnessDot = ({ freshness }) => {
  const map = {
    current:     { color: "var(--brand-lime, #B7E31D)", label: "Current" },
    stale:       { color: "var(--amber-hold, #D79A27)", label: "Stale" },
    no_location: { color: "var(--steel, #6B747C)",       label: "No location" },
  };
  const it = map[freshness] || map.no_location;
  return (
    <span
      title={it.label}
      className="inline-block w-1.5 h-1.5 rounded-full mr-1.5 align-middle"
      style={{ background: it.color }}
    />
  );
};

export default function FleetMapWorkspace() {
  const nav = useNavigate();
  const { rows, fetchedAt, isRefreshing, refreshError, refresh, canManualRefresh } = useFleetLocations();

  const [filter, setFilter] = useState("all");
  const [selectedAssetId, setSelectedAssetId] = useState(null);
  const [focusMarkerId, setFocusMarkerId] = useState(null);
  const [fitNonce, setFitNonce] = useState(0);
  const [userInteracted, setUserInteracted] = useState(false);

  const filteredRows = useMemo(() => {
    const pred = FILTER_PREDICATES[filter] || FILTER_PREDICATES.all;
    return rows.filter(pred);
  }, [rows, filter]);

  const locatedRows = useMemo(
    () => filteredRows.filter((r) => r.has_location),
    [filteredRows]
  );

  const markers = useMemo(() => locatedRows.map((r) => ({
    id: r.asset_id,
    lat: r.lat,
    lng: r.lng,
    label: r.coach_label || r.fleet_number,
    info: buildInfoHtml(r),
  })), [locatedRows]);

  // Fit Fleet: re-fit the map to CURRENT filtered markers. Also clears
  // the "user has panned" flag so subsequent filter changes fit again.
  const onFitFleet = useCallback(() => {
    setUserInteracted(false);
    setFitNonce((n) => n + 1);
  }, []);

  // Filter change → re-fit. Only when the user hasn't panned OR when
  // they explicitly clicked Fit Fleet just before. If they've panned
  // then chosen a different filter, we still refit because the filter
  // is an intentional viewport-changing action per the brief.
  const onFilterChange = useCallback((next) => {
    setFilter(next);
    setUserInteracted(false);
    setFitNonce((n) => n + 1);
  }, []);

  // Marker → row select (external row highlight + smooth scroll).
  const onMarkerClick = useCallback((assetId) => {
    setSelectedAssetId(assetId);
    // Scroll the corresponding row into view — but the map viewport is
    // untouched (marker click keeps the map camera stable).
    requestAnimationFrame(() => {
      const el = document.querySelector(`[data-testid="fleet-loc-row-${assetId}"]`);
      el?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    });
  }, []);

  // Row → map focus + InfoWindow. If the row has no coords, we DO NOT
  // move the map — we only highlight the row (per brief).
  const onRowClick = useCallback((row) => {
    setSelectedAssetId(row.asset_id);
    if (row.has_location) {
      // Bump focusMarkerId (add a nonce suffix inside a distinct key to
      // force effect re-run even when the same asset is re-clicked).
      setFocusMarkerId(row.asset_id);
      // Reset the sentinel on next microtask so re-selecting works.
      setTimeout(() => setFocusMarkerId(null), 400);
    }
  }, []);

  const withLocationCount = locatedRows.length;
  const totalCount = filteredRows.length;

  return (
    <RoleGate allow={READ_ROLES}>
      <PageShell>
        <PageHeader
          testid="page-fleet-map"
          title="Fleet Map"
          subtitle={`${withLocationCount.toLocaleString()} coach${withLocationCount === 1 ? "" : "es"} on live telemetry · ${totalCount.toLocaleString()} in view`}
          right={
            <div className="flex items-center gap-2">
              <button
                type="button"
                data-testid="fleet-map-back"
                onClick={() => nav("/fleet")}
                className="inline-flex items-center gap-1.5 border border-coach-silver hover:border-coach-blue text-[12.5px] px-2 py-1.5 rounded-sm"
              >
                <ArrowLeft size={13} /> Fleet List
              </button>
            </div>
          }
        />

        {/* Filter bar + refresh */}
        <div className="mt-3 bg-white border border-coach-silver rounded-md">
          <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-b border-coach-silver">
            {FILTER_LABELS.map(([key, label]) => (
              <button
                key={key}
                type="button"
                data-testid={`fleet-map-filter-${key}`}
                onClick={() => onFilterChange(key)}
                className={`text-[12px] px-2.5 py-1 rounded-sm border ${
                  filter === key
                    ? "bg-coach-blue text-white border-coach-blue"
                    : "border-coach-silver text-carbon hover:border-coach-blue"
                }`}
              >
                {label}
              </button>
            ))}
            <div className="flex-1" />
            <button
              type="button"
              data-testid="fleet-map-fit-fleet"
              onClick={onFitFleet}
              className="inline-flex items-center gap-1.5 border border-coach-silver hover:border-coach-blue text-[12.5px] px-2 py-1 rounded-sm"
              title="Fit map to all visible coaches"
            >
              <Maximize2 size={12} /> Fit Fleet
            </button>
            <button
              type="button"
              data-testid="fleet-map-refresh"
              onClick={() => refresh({})}
              disabled={!canManualRefresh}
              className="inline-flex items-center gap-1.5 bg-coach-blue hover:bg-deep-road-navy disabled:opacity-40 text-white text-[12.5px] px-2 py-1 rounded-sm"
              title={canManualRefresh ? "Refresh locations" : "Please wait…"}
            >
              <RefreshCw size={12} className={isRefreshing ? "animate-spin" : ""} />
              Refresh Locations
            </button>
          </div>

          {refreshError && (
            <div
              className="px-4 py-2 border-b border-coach-silver bg-amber-hold/10 text-[12px] text-carbon flex items-center justify-between gap-4"
              data-testid="fleet-map-refresh-error"
            >
              <span>Location refresh failed — showing last known positions.</span>
              <button
                type="button"
                onClick={() => refresh({ force: true })}
                className="text-coach-blue hover:underline"
              >
                Retry
              </button>
            </div>
          )}

          {/* Large map */}
          <div
            className="relative w-full"
            style={{ height: "min(560px, 60vh)", minHeight: 360 }}
            data-testid="fleet-map-container"
          >
            {markers.length === 0 ? (
              <div className="absolute inset-0 flex items-center justify-center text-[13px] text-steel">
                {rows.length === 0 && !fetchedAt
                  ? "Loading fleet locations…"
                  : "No coaches with current location for this filter."}
              </div>
            ) : (
              <GoogleMap
                markers={markers}
                fit="controlled"
                fitNonce={fitNonce}
                zoom={9}
                focusMarkerId={focusMarkerId}
                onMarkerClick={onMarkerClick}
                onUserInteract={() => setUserInteracted(true)}
                containerStyle={{ position: "absolute", inset: 0 }}
              />
            )}
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2 border-t border-coach-silver text-[11.5px] text-steel">
            <div data-testid="fleet-map-fetch-time">
              {fetchedAt ? `Locations refreshed at ${fmtTime(fetchedAt)}` : "—"}
              {userInteracted && (
                <span className="ml-2 uppercase tracking-widest text-[10.5px]">
                  · Viewport locked — Fit Fleet to reset
                </span>
              )}
            </div>
            <div>Auto-refresh every 15 min · Motive telemetry timestamps below are authoritative</div>
          </div>
        </div>

        {/* Location list */}
        <section className="mt-5">
          <div className="flex items-center gap-2 mb-3">
            <div className="text-[11px] uppercase tracking-widest text-steel font-semibold">
              Current Asset Locations
            </div>
            <div className="flex-1 h-px bg-chrome-mist" />
            <div className="text-[10.5px] text-steel">{filteredRows.length}</div>
          </div>

          <div className="bg-white border border-coach-silver rounded-md overflow-hidden">
            <table className="hidden md:table w-full text-[13px]" data-testid="fleet-map-locations-table">
              <thead className="bg-road-white text-[10.5px] uppercase tracking-widest text-steel">
                <tr>
                  <th className="text-left px-4 py-2">Coach</th>
                  <th className="text-left px-4 py-2 w-32">Status</th>
                  <th className="text-left px-4 py-2">Location</th>
                  <th className="text-left px-4 py-2 w-24">Speed</th>
                  <th className="text-left px-4 py-2 w-20">Ignition</th>
                  <th className="text-left px-4 py-2 w-40">Driver</th>
                  <th className="text-left px-4 py-2 w-40">Motive Reported</th>
                  <th className="px-2 py-2 w-8" />
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((r) => {
                  const isSelected = r.asset_id === selectedAssetId;
                  return (
                    <tr
                      key={r.asset_id}
                      data-testid={`fleet-loc-row-${r.asset_id}`}
                      data-selected={isSelected ? "true" : undefined}
                      className={`border-t border-coach-silver/60 cursor-pointer ${
                        isSelected ? "bg-coach-blue/10" : "hover:bg-road-white"
                      }`}
                      onClick={() => onRowClick(r)}
                    >
                      <td className="px-4 py-2">
                        <FreshnessDot freshness={r.freshness} />
                        <Link
                          to={`/fleet/${r.asset_id}`}
                          className="text-coach-blue hover:underline"
                          onClick={(e) => e.stopPropagation()}
                          data-testid={`fleet-loc-name-${r.asset_id}`}
                        >
                          {r.coach_label || r.unit_name || r.fleet_number}
                        </Link>{" "}
                        <span className="font-mono text-[11px] text-steel">
                          #{r.fleet_number}
                        </span>
                      </td>
                      <td className="px-4 py-2"><StatusPill status={r.operational_status} /></td>
                      <td className="px-4 py-2 text-steel">
                        {r.has_location
                          ? (r.location_label || `${r.lat?.toFixed?.(3)}, ${r.lng?.toFixed?.(3)}`)
                          : <span className="text-[12px]">Location unavailable</span>}
                      </td>
                      <td className="px-4 py-2 text-steel tabular-nums">
                        {typeof r.speed_mph === "number" ? `${r.speed_mph} mph` : "—"}
                      </td>
                      <td className="px-4 py-2 text-steel">
                        {r.ignition_on == null ? "—" : (r.ignition_on ? "On" : "Off")}
                      </td>
                      <td className="px-4 py-2 text-steel">
                        {r.current_driver?.display_name || "—"}
                      </td>
                      <td className="px-4 py-2 text-steel">
                        {r.last_updated_at
                          ? `${fmtTime(r.last_updated_at)} · ${relativeAgeLabel(r.last_updated_at)}`
                          : "—"}
                      </td>
                      <td className="px-2 py-2 text-right text-steel">
                        <ChevronRight size={14} />
                      </td>
                    </tr>
                  );
                })}
                {filteredRows.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-4 py-10 text-center text-steel">
                      No coaches match this filter.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            {/* Mobile cards */}
            <div className="md:hidden divide-y divide-coach-silver/60" data-testid="fleet-map-locations-mobile">
              {filteredRows.map((r) => {
                const isSelected = r.asset_id === selectedAssetId;
                return (
                  <div
                    key={r.asset_id}
                    data-testid={`fleet-loc-card-${r.asset_id}`}
                    data-selected={isSelected ? "true" : undefined}
                    className={`p-3 cursor-pointer ${isSelected ? "bg-coach-blue/10" : ""}`}
                    onClick={() => onRowClick(r)}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5">
                          <FreshnessDot freshness={r.freshness} />
                          <span className="font-medium text-[14px]">{r.coach_label || r.fleet_number}</span>
                        </div>
                        <div className="mt-0.5 text-[12px] text-steel truncate">
                          {r.has_location ? (r.location_label || "—") : "Location unavailable"}
                        </div>
                      </div>
                      <StatusPill status={r.operational_status} />
                    </div>
                    <div className="mt-1.5 text-[11.5px] text-steel flex flex-wrap gap-x-3 gap-y-0.5">
                      {typeof r.speed_mph === "number" && <span>{r.speed_mph} mph</span>}
                      {r.ignition_on !== null && r.ignition_on !== undefined && (
                        <span>Ignition {r.ignition_on ? "on" : "off"}</span>
                      )}
                      {r.current_driver?.display_name && <span>{r.current_driver.display_name}</span>}
                      {r.last_updated_at && <span>{relativeAgeLabel(r.last_updated_at)}</span>}
                    </div>
                  </div>
                );
              })}
              {filteredRows.length === 0 && (
                <div className="p-6 text-center text-steel text-[13px]">
                  No coaches match this filter.
                </div>
              )}
            </div>
          </div>
        </section>
      </PageShell>
    </RoleGate>
  );
}
